/**
 * "Where did my money go?" full breakdown page — /portal/cash-out.
 * Same data as the tile but with a donut chart, all categories, and inline
 * education about what each category means.
 */
import { useEffect, useState } from "react";
import { Wallet, TrendingUp, TrendingDown, AlertTriangle, Info, Loader2, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const NZD_2 = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

function hueFor(name) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = ((h * 31) + name.charCodeAt(i)) & 0xffff;
    return h % 360;
}

export default function PortalCashOut() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/spend-insights", { params: { top_n: 20 } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded) {
        return <div className="text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>;
    }
    if (!data || !data.has_data) {
        return (
            <div className="co-card co-card-pad text-center" data-testid="cash-out-empty">
                <Wallet className="mx-auto h-8 w-8 text-[#5E636E]" />
                <div className="mt-3 font-medium">No spend data yet</div>
                <div className="mt-1 text-sm text-[#5E636E]">
                    Once your accountant syncs your books, your cash-out breakdown will appear here.
                </div>
            </div>
        );
    }

    const rows = [...(data.categories || [])];
    if (data.other) rows.push({ ...data.other, __isOther: true });
    const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <div className="space-y-6" data-testid="portal-cash-out">
            <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Cash out</div>
                <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold text-[#111]">Where your money went</h1>
                <p className="mt-1 text-sm text-[#5E636E]">
                    Last 90 days · {data.category_count} spend categor{data.category_count === 1 ? "y" : "ies"}
                    {data.updated_at ? ` · updated ${new Date(data.updated_at).toLocaleDateString("en-NZ")}` : ""}
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
                <div className="co-card co-card-pad flex flex-col items-center justify-center">
                    <Donut rows={rows} total={total} />
                    <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Total spent</div>
                    <div className="text-2xl font-display font-semibold tabular-nums text-[#111]">{NZD.format(total)}</div>
                    {data.totals?.revenue != null && (
                        <div className="mt-1 text-xs text-[#5E636E]">
                            Revenue <strong className="text-[#111] tabular-nums">{NZD.format(data.totals.revenue)}</strong>
                            {" · "}
                            Net profit <strong className="text-[#111] tabular-nums">{NZD.format(data.totals.net_profit || 0)}</strong>
                        </div>
                    )}
                </div>

                <div className="co-card">
                    <ul className="divide-y divide-[#E4E4E7]" data-testid="cash-out-categories">
                        {rows.map((c) => (
                            <li key={c.name} className="px-4 py-3 flex items-center gap-3">
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: `hsl(${c.__isOther ? 220 : hueFor(c.name)} 55% 55%)` }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-[#111] truncate">{c.name}</div>
                                    <div className="text-[11px] text-[#5E636E]">
                                        {c.__isOther
                                            ? `${c.line_count} smaller categories combined`
                                            : c.prior_amount != null
                                                ? `Prior 90d: ${NZD_2.format(c.prior_amount)}`
                                                : "No prior period data to compare against yet"}
                                    </div>
                                </div>
                                <div className="text-right tabular-nums">
                                    <div className="text-sm font-semibold text-[#111]">{NZD_2.format(c.amount)}</div>
                                    <div className="text-[11px] text-[#5E636E]">{c.share_pct}% of spend</div>
                                </div>
                                <div className="w-20 text-right">
                                    <ChangePill change={c.change_pct} flag={c.change_flag} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="co-card co-card-pad">
                <div className="flex items-start gap-2 text-sm text-[#5E636E]">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        <strong className="text-[#111]">A word on category names.</strong> We&apos;ve translated your
                        accountant&apos;s Xero labels into plain English where we can. For example,
                        "Motor Vehicle Expenses" becomes "Vehicle costs" and "Cost of Goods Sold" becomes "Stock &amp; supplies".
                        Something look off? <Link to="/portal/messages" className="text-[#0E3B2E] hover:underline inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Ask your accountant</Link>.
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChangePill({ change, flag }) {
    if (change == null) return <span className="text-[10px] text-[#5E636E]">—</span>;
    if (flag === "up_big") {
        return (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium">
                <AlertTriangle className="h-2.5 w-2.5" /> +{change}%
            </span>
        );
    }
    if (flag === "up") {
        return <span className="inline-flex items-center gap-0.5 text-rose-700 text-[10px] tabular-nums"><TrendingUp className="h-3 w-3" /> +{change}%</span>;
    }
    if (flag === "down") {
        return <span className="inline-flex items-center gap-0.5 text-emerald-700 text-[10px] tabular-nums"><TrendingDown className="h-3 w-3" /> {change}%</span>;
    }
    return <span className="text-[10px] text-[#5E636E]">—</span>;
}

// ---------------------------------------------------------------- Donut
// Pure-SVG donut with proportional arcs. No chart library — keeps bundle lean.
function Donut({ rows, total, size = 180 }) {
    if (!total) return <div className="text-sm text-[#5E636E]">No spend</div>;
    const R = size / 2 - 10;
    const cx = size / 2;
    const cy = size / 2;
    let acc = 0;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="cash-out-donut">
            <circle cx={cx} cy={cy} r={R} fill="none" stroke="#F5F4EF" strokeWidth={22} />
            {rows.map((c) => {
                const frac = (c.amount || 0) / total;
                if (frac < 0.001) return null;
                const start = acc;
                acc += frac;
                const end = acc;
                const largeArc = frac > 0.5 ? 1 : 0;
                const x1 = cx + R * Math.cos(2 * Math.PI * start - Math.PI / 2);
                const y1 = cy + R * Math.sin(2 * Math.PI * start - Math.PI / 2);
                const x2 = cx + R * Math.cos(2 * Math.PI * end - Math.PI / 2);
                const y2 = cy + R * Math.sin(2 * Math.PI * end - Math.PI / 2);
                const hue = c.__isOther ? 220 : hueFor(c.name);
                return (
                    <path
                        key={c.name}
                        d={`M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`}
                        fill="none"
                        stroke={`hsl(${hue} 55% 55%)`}
                        strokeWidth={22}
                        strokeLinecap="butt"
                    />
                );
            })}
        </svg>
    );
}
